import React from "react";
import { FaThumbtack, FaTrash, FaPen, FaRegCalendarAlt } from "react-icons/fa";
import styles from "./SavedLessonPlanCard.module.css";

const SavedLessonPlanCard = ({
  id,
  title,
  date,
  time,
  status = "Draft",
  isSelected = false,
  isPinned = false,
  // NEW: when true, Schedule is disabled on EVERY card (driven by parent
  // checking if any card in item_list is Approved / Completed / Submitted)
  disableScheduleGlobally = false,
  disableActions = false,
  onSelect,
  onPin,
  onDelete,
  onEdit,
  onSchedule,
  actions,
  translations = {},
}) => {
  const t = translations;

  const normalizedStatus = (status || "").toLowerCase();

  const isActionDisabled = (actionKey) => {
    // Parent-driven hard disable for delete / edit / schedule
    // (Previous Years view = read-only actions)
    if (disableActions && ["delete", "edit", "schedule"].includes(actionKey)) {
      return true;
    }

    // Global override for Schedule — disables Schedule on every card
    if (actionKey === "schedule" && disableScheduleGlobally) {
      return true;
    }

    // Approved / Completed → disable Delete, Edit, Schedule
    if (normalizedStatus === "approved" || normalizedStatus === "completed") {
      return ["delete", "edit", "schedule"].includes(actionKey);
    }

    // Rejected → disable Delete only
    if (normalizedStatus === "rejected") {
      return actionKey === "delete";
    }

    return false;
  };

  const defaultActions = [
    {
      key: "delete",
      icon: <FaTrash />,
      label: t.delete || "Delete",
      onClick: (cardId) => onDelete?.(cardId),
    },
    {
      key: "edit",
      icon: <FaPen />,
      label: t.edit || "Edit",
      onClick: (cardId) => onEdit?.(cardId),
    },
    {
      key: "schedule",
      icon: <FaRegCalendarAlt />,
      label: t.schedule || "Schedule",
      onClick: (cardId) => onSchedule?.(cardId),
    },
  ];

  const renderActions = (actions || defaultActions).map((a) => ({
    ...a,
    disabled: a.disabled !== undefined ? a.disabled : isActionDisabled(a.key),
  }));

  // Status badge class
  const getStatusClass = () => {
    switch (normalizedStatus) {
      case "submitted":
        return styles.statusSubmitted;
      case "approved":
        return styles.statusApproved;
      case "rejected":
        return styles.statusRejected;
      case "resubmitted":
      case "re-submitted":
        return styles.statusResubmitted;
      case "completed":
        return styles.statusCompleted;
      case "draft":
      default:
        return styles.statusDraft;
    }
  };

  const handleCardClick = () => {
    if (typeof onSelect === "function") onSelect(id);
  };

  const handlePinClick = (e) => {
    e.stopPropagation();
    if (typeof onPin === "function") onPin(id);
  };

  const handleActionClick = (e, action) => {
    e.stopPropagation();
    if (action.disabled) return;
    if (typeof action.onClick === "function") action.onClick(id);
  };

  return (
    <div
      className={`${styles.savedCard} ${
        isSelected ? styles.savedCardSelected : ""
      }`}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleCardClick();
      }}
    >
      {/* Header: title + pin */}
      <div className={styles.savedCardHeader}>
        <span
          className={`${styles.savedCardTitle} ${
            isSelected ? styles.savedCardTitleSelected : ""
          }`}
        >
          {title}
        </span>
        <button
          type="button"
          className={`${styles.pinButton} ${
            isPinned ? styles.pinButtonActive : ""
          }`}
          onClick={handlePinClick}
          aria-label="Pin"
        >
          <FaThumbtack />
        </button>
      </div>

      {/* Meta row: date/time + status */}
      <div className={styles.savedCardMeta}>
        <div className={styles.savedCardDateTime}>
          <span className={styles.savedCardDate}>{date}</span>
          {time && (
            <>
              <span className={styles.savedCardDivider}>|</span>
              <span className={styles.savedCardTime}>{time}</span>
            </>
          )}
        </div>
        {/* <span className={`${styles.statusBadge} ${getStatusClass()}`}>
          {status}
        </span> */}
      </div>

      {/* Actions row */}
      <div className={styles.savedCardActions}>
        {renderActions.map((action, idx) => (
          <React.Fragment key={action.key}>
            <button
              type="button"
              className={`${styles.savedCardActionBtn} ${
                action.key === "delete" ? styles.savedCardActionDelete : ""
              } ${action.disabled ? styles.savedCardActionDisabled : ""}`}
              onClick={(e) => handleActionClick(e, action)}
              disabled={action.disabled}
              title={action.label}
            >
              <span className={styles.savedCardActionIcon}>{action.icon}</span>
              <span>{action.label}</span>
            </button>
            {idx < renderActions.length - 1 && (
              <span className={styles.savedCardActionDivider} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default SavedLessonPlanCard;
